import {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useOutletContext } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import {
  buildTree,
  flattenTree,
  findNodeInTree,
} from '../../lib/taxonomy';
import {
  LANE_HEIGHT,
  CANVAS_W,
  DEFAULT_LANES,
  PRESETS,
  BUILTIN_SHAPE_DEFS,
  BASE_GEOMETRIES,
  FLOW_STATUS_LABEL,
  presetByKey,
  luminanceText,
  shapeDef,
  shapeSize,
  shapeCenter,
  deltaClass,
  flowStatusBadgeClass,
  dbShapeToCanvas,
  canvasShapeToDb,
  downloadFile,
  exportTransformaJson,
  exportBpmn,
  exportMermaid,
  parseTransformaImport,
  prepareProcessDesignImport,
} from '../../lib/processDesign';
import './ProcessDesign.css';

const EMPTY_DIAGRAM = () => ({ shapes: [], connections: [] });

function connFromDb(c) {
  return {
    id: c.id,
    from: c.from_shape_id,
    to: c.to_shape_id,
    label: c.label || '',
  };
}

function buildNodeFlowBundle(flowRows, buildItemsById) {
  const bundle = {
    lanes: [...DEFAULT_LANES],
    current: { flowId: null, status: 'not_drafted', ...EMPTY_DIAGRAM() },
    future: { flowId: null, status: 'not_drafted', ...EMPTY_DIAGRAM() },
  };
  for (const f of flowRows || []) {
    const side = f.state === 'current' ? 'current' : 'future';
    bundle[side] = {
      flowId: f.id,
      status: f.status,
      shapes: (f.process_flow_shapes || []).map((s) => dbShapeToCanvas(s, buildItemsById)),
      connections: (f.process_flow_connections || []).map(connFromDb),
    };
    if (Array.isArray(f.lanes) && f.lanes.length) bundle.lanes = f.lanes;
  }
  return bundle;
}

export default function ProcessDesign() {
  const { project, programme } = useOutletContext();
  const { canWrite } = useWorkspaceAccess();
  const orgId = programme?.organisation_id;
  const readOnly = !canWrite;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [inScopeSet, setInScopeSet] = useState(new Set());
  const [flowsByNode, setFlowsByNode] = useState({});
  const [customShapeTypes, setCustomShapeTypes] = useState([]);
  const [buildItems, setBuildItems] = useState([]);
  const [activeDomainId, setActiveDomainId] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [activeView, setActiveView] = useState('future');
  const [activeDepth, setActiveDepth] = useState(3);
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [connectingFromId, setConnectingFromId] = useState(null);
  const [lanePanelOpen, setLanePanelOpen] = useState(false);
  const [exportPanelOpen, setExportPanelOpen] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customBase, setCustomBase] = useState('rect');
  const [customColorKey, setCustomColorKey] = useState('purple');
  const [connDeleteId, setConnDeleteId] = useState(null);
  const [connDeletePos, setConnDeletePos] = useState(null);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [toast, setToast] = useState(null);
  const dragCtx = useRef(null);
  const importRef = useRef(null);

  const tree = useMemo(() => buildTree(nodes), [nodes]);

  const domainTabs = useMemo(() => {
    return tree.filter((l1) => {
      const desc = flattenTree(l1.children || []);
      return desc.some((n) => inScopeSet.has(n.id)) || inScopeSet.has(l1.id);
    });
  }, [tree, inScopeSet]);

  const showToast = useCallback((msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const load = useCallback(async () => {
    if (!project?.id || !orgId) return;
    setLoading(true);
    try {
      const { data: pinRow } = await sb
        .from('project_taxonomy_pins')
        .select('taxonomy_version_id')
        .eq('project_id', project.id)
        .maybeSingle();

      let nodeRows = [];
      let scopeIds = new Set();
      if (pinRow?.taxonomy_version_id) {
        const [{ data: nRows }, { data: scope }] = await Promise.all([
          sb.from('org_process_hierarchy')
            .select('*')
            .eq('taxonomy_version_id', pinRow.taxonomy_version_id)
            .order('level')
            .order('order_index'),
          sb.from('project_process_scope_nodes')
            .select('process_node_id, in_scope')
            .eq('project_id', project.id)
            .eq('in_scope', true),
        ]);
        nodeRows = nRows || [];
        scopeIds = new Set((scope || []).map((r) => r.process_node_id));
      }

      const [flowRes, customRes, biRes] = await Promise.all([
        sb.from('process_flows')
          .select(`
            id, project_id, process_node_id, state, status, lanes,
            process_flow_shapes (*),
            process_flow_connections (*)
          `)
          .eq('project_id', project.id),
        sb.from('org_custom_shape_types')
          .select('*')
          .eq('organisation_id', orgId)
          .order('order_index'),
        sb.from('deliverables')
          .select('id, ref, title')
          .eq('project_id', project.id)
          .eq('kind', 'build_item')
          .order('ref'),
      ]);

      const biList = biRes.data || [];
      const biMap = new Map(biList.map((b) => [b.id, b]));
      const byNode = {};
      for (const f of flowRes.data || []) {
        if (!byNode[f.process_node_id]) byNode[f.process_node_id] = [];
        byNode[f.process_node_id].push(f);
      }
      const flows = {};
      for (const [nodeId, rows] of Object.entries(byNode)) {
        flows[nodeId] = buildNodeFlowBundle(rows, biMap);
      }

      setNodes(nodeRows);
      setInScopeSet(scopeIds);
      setFlowsByNode(flows);
      setCustomShapeTypes(customRes.data || []);
      setBuildItems(biList);

      const domains = buildTree(nodeRows).filter((l1) => {
        const desc = flattenTree(l1.children || []);
        return desc.some((n) => scopeIds.has(n.id));
      });
      const firstDomain = domains[0]?.id || null;
      setActiveDomainId((prev) => prev || firstDomain);
    } catch (e) {
      console.error('[ProcessDesign] load failed', e);
      showToast('Failed to load process design');
    } finally {
      setLoading(false);
    }
  }, [project?.id, orgId, showToast]);

  useEffect(() => { load(); }, [load]);

  const nodeBundle = selectedNodeId ? flowsByNode[selectedNodeId] : null;
  const selectedNode = selectedNodeId ? findNodeInTree(tree, selectedNodeId) : null;
  const parentName = selectedNode?.parent_id
    ? nodes.find((n) => n.id === selectedNode.parent_id)?.name
    : null;

  const lanes = nodeBundle?.lanes || DEFAULT_LANES;
  const futureStatus = nodeBundle?.future?.status || 'not_drafted';
  const diagramState = activeView === 'delta' ? 'future' : activeView;
  const activeDiagram = nodeBundle?.[diagramState] || EMPTY_DIAGRAM();
  const isReadOnlyCanvas = readOnly || activeView === 'delta';

  const updateNodeBundle = useCallback((nodeId, patchFn) => {
    setFlowsByNode((prev) => {
      const current = prev[nodeId] || {
        lanes: [...DEFAULT_LANES],
        current: { flowId: null, status: 'not_drafted', ...EMPTY_DIAGRAM() },
        future: { flowId: null, status: 'not_drafted', ...EMPTY_DIAGRAM() },
      };
      return { ...prev, [nodeId]: patchFn(current) };
    });
  }, []);

  const ensureFlowRow = useCallback(async (nodeId, state, lanesOverride) => {
    const existing = flowsByNode[nodeId]?.[state]?.flowId;
    if (existing) return existing;
    const lanesVal = lanesOverride ?? flowsByNode[nodeId]?.lanes ?? DEFAULT_LANES;
    const { data, error } = await sb.from('process_flows')
      .upsert({
        project_id: project.id,
        process_node_id: nodeId,
        state,
        status: state === 'future' ? 'not_drafted' : 'not_drafted',
        lanes: lanesVal,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'project_id,process_node_id,state' })
      .select('id')
      .single();
    if (error) throw error;
    updateNodeBundle(nodeId, (b) => ({
      ...b,
      lanes: lanesVal,
      [state]: { ...b[state], flowId: data.id },
    }));
    return data.id;
  }, [flowsByNode, project?.id, updateNodeBundle]);

  const syncLanesToDb = useCallback(async (nodeId, newLanes) => {
    const bundle = flowsByNode[nodeId];
    const updates = [];
    if (bundle?.current?.flowId) {
      updates.push(sb.from('process_flows').update({ lanes: newLanes, updated_at: new Date().toISOString() }).eq('id', bundle.current.flowId));
    }
    if (bundle?.future?.flowId) {
      updates.push(sb.from('process_flows').update({ lanes: newLanes, updated_at: new Date().toISOString() }).eq('id', bundle.future.flowId));
    }
    if (!updates.length) {
      await ensureFlowRow(nodeId, 'current', newLanes);
      await ensureFlowRow(nodeId, 'future', newLanes);
    } else {
      await Promise.all(updates);
    }
  }, [flowsByNode, ensureFlowRow]);

  const persistShape = useCallback(async (nodeId, state, shape) => {
    const flowId = flowsByNode[nodeId]?.[state]?.flowId || await ensureFlowRow(nodeId, state);
    const payload = canvasShapeToDb(shape, flowId);
    if (shape.id && !String(shape.id).startsWith('new_')) {
      const { error } = await sb.from('process_flow_shapes').update(payload).eq('id', shape.id);
      if (error) throw error;
      return shape.id;
    }
    const { data, error } = await sb.from('process_flow_shapes').insert(payload).select('id').single();
    if (error) throw error;
    return data.id;
  }, [flowsByNode, ensureFlowRow]);

  const treeRows = useMemo(() => {
    if (!activeDomainId) return [];
    const l1 = tree.find((n) => n.id === activeDomainId);
    if (!l1) return [];
    const rows = [];
    const maxLevel = activeDepth === 4 ? 4 : 3;
    const walk = (node, depth) => {
      if (node.level === 2) {
        rows.push({ node, selectable: false, levelClass: 'l2' });
      } else if (node.level >= 3 && node.level <= maxLevel && inScopeSet.has(node.id)) {
        const status = flowsByNode[node.id]?.future?.status || 'not_drafted';
        rows.push({ node, selectable: true, levelClass: node.level === 3 ? 'l3' : 'l4', status });
      }
      if (node.level < maxLevel) {
        (node.children || []).forEach((c) => walk(c, depth + 1));
      }
    };
    (l1.children || []).forEach((c) => walk(c, 0));
    return rows;
  }, [tree, activeDomainId, activeDepth, inScopeSet, flowsByNode]);

  useEffect(() => {
    if (!selectedNodeId && treeRows.length) {
      const first = treeRows.find((r) => r.selectable);
      if (first) setSelectedNodeId(first.node.id);
    }
  }, [treeRows, selectedNodeId]);

  function selectNode(id) {
    setSelectedNodeId(id);
    setSelectedShapeId(null);
    setConnectingFromId(null);
    setLanePanelOpen(false);
    setExportPanelOpen(false);
    setConnDeleteId(null);
  }

  function setView(v) {
    setActiveView(v);
    setSelectedShapeId(null);
    setConnectingFromId(null);
    setConnDeleteId(null);
  }

  async function addShape(type) {
    if (isReadOnlyCanvas || !selectedNodeId) {
      if (activeView === 'delta') showToast('Switch to Current or Future state to edit');
      return;
    }
    const def = shapeDef(type, customShapeTypes);
    if (!def) return;
    const state = activeView;
    const lane = def.base === 'note' ? null : lanes[0];
    const shape = {
      id: `new_${Date.now()}`,
      type,
      label: def.label,
      lane,
      x: 200 + Math.random() * 80,
      y: lane === null ? 40 : 12,
      color: null,
    };
    setSaving(true);
    try {
      const realId = await persistShape(selectedNodeId, state, shape);
      const saved = { ...shape, id: realId };
      updateNodeBundle(selectedNodeId, (b) => ({
        ...b,
        [state]: {
          ...b[state],
          shapes: [...b[state].shapes, saved],
          status: b[state].status === 'not_drafted' && state === 'future' ? 'draft' : b[state].status,
        },
      }));
      if (state === 'future' && flowsByNode[selectedNodeId]?.future?.flowId) {
        await sb.from('process_flows').update({ status: 'draft', updated_at: new Date().toISOString() })
          .eq('id', flowsByNode[selectedNodeId].future.flowId);
      }
      setSelectedShapeId(realId);
      setEditingLabelId(realId);
    } catch (e) {
      showToast(e.message || 'Could not add shape');
    } finally {
      setSaving(false);
    }
  }

  async function deleteShape(id) {
    if (isReadOnlyCanvas || !selectedNodeId) return;
    const state = activeView;
    setSaving(true);
    try {
      await sb.from('process_flow_shapes').delete().eq('id', id);
      updateNodeBundle(selectedNodeId, (b) => ({
        ...b,
        [state]: {
          ...b[state],
          shapes: b[state].shapes.filter((s) => s.id !== id),
          connections: b[state].connections.filter((c) => c.from !== id && c.to !== id),
        },
      }));
      if (selectedShapeId === id) setSelectedShapeId(null);
    } catch (e) {
      showToast(e.message || 'Could not delete shape');
    } finally {
      setSaving(false);
    }
  }

  async function updateShapeFields(id, patch) {
    if (!selectedNodeId) return;
    const state = activeView;
    updateNodeBundle(selectedNodeId, (b) => ({
      ...b,
      [state]: {
        ...b[state],
        shapes: b[state].shapes.map((s) => (s.id === id ? { ...s, ...patch } : s)),
      },
    }));
    const shape = { ...activeDiagram.shapes.find((s) => s.id === id), ...patch };
    if (shape && !readOnly) {
      try {
        await persistShape(selectedNodeId, state, shape);
      } catch (e) {
        showToast(e.message || 'Could not save shape');
      }
    }
  }

  async function addConnection(fromId, toId, label) {
    if (!selectedNodeId || readOnly) return;
    const state = activeView;
    setSaving(true);
    try {
      const flowId = flowsByNode[selectedNodeId]?.[state]?.flowId || await ensureFlowRow(selectedNodeId, state);
      const { data, error } = await sb.from('process_flow_connections').insert({
        process_flow_id: flowId,
        from_shape_id: fromId,
        to_shape_id: toId,
        label: label || null,
      }).select('id').single();
      if (error) throw error;
      const conn = { id: data.id, from: fromId, to: toId, label: label || '' };
      updateNodeBundle(selectedNodeId, (b) => ({
        ...b,
        [state]: { ...b[state], connections: [...b[state].connections, conn] },
      }));
    } catch (e) {
      showToast(e.message || 'Could not add connection');
    } finally {
      setSaving(false);
    }
  }

  async function deleteConnection(id) {
    if (!selectedNodeId || readOnly) return;
    const state = activeView;
    try {
      await sb.from('process_flow_connections').delete().eq('id', id);
      updateNodeBundle(selectedNodeId, (b) => ({
        ...b,
        [state]: { ...b[state], connections: b[state].connections.filter((c) => c.id !== id) },
      }));
      setConnDeleteId(null);
    } catch (e) {
      showToast(e.message || 'Could not delete connection');
    }
  }

  function handleShapeClick(id) {
    if (connectingFromId && connectingFromId !== id) {
      const fromShape = activeDiagram.shapes.find((s) => s.id === connectingFromId);
      let label = '';
      if (fromShape && (shapeDef(fromShape.type, customShapeTypes) || {}).base === 'diamond') {
        const existing = activeDiagram.connections.filter((c) => c.from === connectingFromId).length;
        // TODO(marco): inline popover instead of window.prompt for branch labels
        label = window.prompt('Branch label for this connection (e.g. Yes / No):', existing === 0 ? 'Yes' : 'No') || '';
      }
      addConnection(connectingFromId, id, label);
      setConnectingFromId(null);
      setSelectedShapeId(id);
      return;
    }
    setSelectedShapeId((prev) => (prev === id ? null : id));
    setConnDeleteId(null);
  }

  function onShapeMouseDown(e, shape) {
    if (isReadOnlyCanvas || editingLabelId === shape.id) return;
    e.preventDefault();
    dragCtx.current = {
      id: shape.id,
      startX: e.clientX,
      startY: e.clientY,
      origX: shape.x,
      origY: shape.y,
      moved: false,
    };
    const onMove = (ev) => {
      if (!dragCtx.current) return;
      const dx = ev.clientX - dragCtx.current.startX;
      const dy = ev.clientY - dragCtx.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragCtx.current.moved = true;
      let newX = Math.max(4, dragCtx.current.origX + dx);
      let newY = Math.max(4, dragCtx.current.origY + dy);
      let newLane = shape.lane;
      if (newLane !== null && newLane !== undefined) {
        const laneIdx = Math.min(lanes.length - 1, Math.max(0, Math.floor(newY / LANE_HEIGHT)));
        newLane = lanes[laneIdx];
      }
      updateNodeBundle(selectedNodeId, (b) => ({
        ...b,
        [activeView]: {
          ...b[activeView],
          shapes: b[activeView].shapes.map((s) => (
            s.id === shape.id ? { ...s, x: newX, y: newY, lane: newLane } : s
          )),
        },
      }));
    };
    const onUp = async () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (dragCtx.current?.moved) {
        setSelectedShapeId(dragCtx.current.id);
        const updated = flowsByNode[selectedNodeId]?.[activeView]?.shapes.find((s) => s.id === dragCtx.current.id);
        if (updated) {
          try {
            await persistShape(selectedNodeId, activeView, updated);
          } catch (err) {
            showToast(err.message || 'Could not save position');
          }
        }
      }
      dragCtx.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  async function setLanes(newLanes) {
    if (!selectedNodeId || readOnly) return;
    updateNodeBundle(selectedNodeId, (b) => ({ ...b, lanes: newLanes }));
    try {
      await syncLanesToDb(selectedNodeId, newLanes);
    } catch (e) {
      showToast(e.message || 'Could not save lanes');
    }
  }

  async function renameLane(index, newName) {
    if (!selectedNodeId || readOnly) return;
    const oldName = lanes[index];
    newName = newName.trim() || oldName;
    if (oldName === newName) return;
    const newLanes = [...lanes];
    newLanes[index] = newName;
    const prior = flowsByNode[selectedNodeId] || {
      lanes: [...DEFAULT_LANES],
      current: { flowId: null, status: 'not_drafted', ...EMPTY_DIAGRAM() },
      future: { flowId: null, status: 'not_drafted', ...EMPTY_DIAGRAM() },
    };
    const patchSide = (side) => ({
      ...prior[side],
      shapes: prior[side].shapes.map((s) => (
        s.lane === oldName ? { ...s, lane: newName } : s
      )),
    });
    const patched = {
      ...prior,
      lanes: newLanes,
      current: patchSide('current'),
      future: patchSide('future'),
    };
    updateNodeBundle(selectedNodeId, () => patched);
    try {
      await syncLanesToDb(selectedNodeId, newLanes);
      for (const side of ['current', 'future']) {
        for (const s of patched[side].shapes) {
          const orig = prior[side].shapes.find((x) => x.id === s.id);
          if (orig?.lane === oldName) {
            await persistShape(selectedNodeId, side, s);
          }
        }
      }
    } catch (e) {
      showToast(e.message || 'Could not rename lane');
    }
  }

  function moveLane(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= lanes.length) return;
    const newLanes = [...lanes];
    [newLanes[i], newLanes[j]] = [newLanes[j], newLanes[i]];
    setLanes(newLanes);
  }

  async function deleteLane(index) {
    if (!selectedNodeId || readOnly) return;
    if (lanes.length <= 1) {
      showToast('A diagram needs at least one swim lane');
      return;
    }
    const name = lanes[index];
    const fallback = lanes.filter((_, i) => i !== index)[0];
    const newLanes = lanes.filter((_, i) => i !== index);
    const prior = flowsByNode[selectedNodeId] || {
      lanes: [...DEFAULT_LANES],
      current: { flowId: null, status: 'not_drafted', ...EMPTY_DIAGRAM() },
      future: { flowId: null, status: 'not_drafted', ...EMPTY_DIAGRAM() },
    };
    let movedCount = 0;
    const patchSide = (side) => ({
      ...prior[side],
      shapes: prior[side].shapes.map((s) => {
        if (s.lane === name) {
          movedCount += 1;
          return { ...s, lane: fallback };
        }
        return s;
      }),
    });
    const patched = {
      ...prior,
      lanes: newLanes,
      current: patchSide('current'),
      future: patchSide('future'),
    };
    updateNodeBundle(selectedNodeId, () => patched);
    try {
      await syncLanesToDb(selectedNodeId, newLanes);
      for (const side of ['current', 'future']) {
        for (const s of patched[side].shapes) {
          const orig = prior[side].shapes.find((x) => x.id === s.id);
          if (orig?.lane === name) {
            await persistShape(selectedNodeId, side, s);
          }
        }
      }
      if (movedCount) showToast(`Lane deleted — ${movedCount} shape(s) moved to "${fallback}"`, 'success');
    } catch (e) {
      showToast(e.message || 'Could not delete lane');
    }
  }

  function addLane() {
    setLanes([...lanes, `New lane ${lanes.length + 1}`]);
  }

  async function acceptDraft() {
    if (!selectedNodeId || readOnly) return;
    const flowId = flowsByNode[selectedNodeId]?.future?.flowId;
    if (!flowId) return;
    try {
      await sb.from('process_flows').update({ status: 'review', updated_at: new Date().toISOString() }).eq('id', flowId);
      updateNodeBundle(selectedNodeId, (b) => ({ ...b, future: { ...b.future, status: 'review' } }));
      showToast('Draft accepted — ready to publish', 'success');
    } catch (e) {
      showToast(e.message || 'Could not accept draft');
    }
  }

  async function publishFlow() {
    if (!selectedNodeId || readOnly) return;
    const status = futureStatus;
    if (!status || status === 'not_drafted') {
      showToast('Nothing to publish for this node yet');
      return;
    }
    const flowId = flowsByNode[selectedNodeId]?.future?.flowId;
    if (!flowId) return;
    try {
      await sb.from('process_flows').update({ status: 'published', updated_at: new Date().toISOString() }).eq('id', flowId);
      updateNodeBundle(selectedNodeId, (b) => ({ ...b, future: { ...b.future, status: 'published' } }));
      showToast('Future-state flow published', 'success');
    } catch (e) {
      showToast(e.message || 'Could not publish');
    }
  }

  async function saveCustomShape() {
    if (!orgId || readOnly) return;
    const label = customLabel.trim() || 'Custom shape';
    const color = presetByKey(customColorKey);
    try {
      const { data, error } = await sb.from('org_custom_shape_types').insert({
        organisation_id: orgId,
        label,
        base_geometry: customBase,
        color,
        order_index: customShapeTypes.length + 1,
      }).select('*').single();
      if (error) throw error;
      setCustomShapeTypes((prev) => [...prev, data]);
      setCustomModalOpen(false);
      showToast(`"${label}" added to the palette — available org-wide`, 'success');
    } catch (e) {
      showToast(e.message || 'Could not save custom shape');
    }
  }

  function handleExportJson() {
    if (!selectedNode) return;
    const current = nodeBundle?.current ?? EMPTY_DIAGRAM();
    const future = nodeBundle?.future ?? EMPTY_DIAGRAM();
    const json = exportTransformaJson({
      nodeId: selectedNodeId,
      nodeName: selectedNode.name,
      lanes,
      diagrams: {
        current: { shapes: current.shapes, connections: current.connections },
        future: { shapes: future.shapes, connections: future.connections },
      },
    });
    downloadFile(json, `${selectedNodeId}-process-flow.json`, 'application/json');
    showToast('Exported Transforma JSON', 'success');
    setExportPanelOpen(false);
  }

  function handleExportBpmn() {
    if (!selectedNode) return;
    const xml = exportBpmn({
      nodeId: selectedNodeId,
      nodeName: selectedNode.name,
      lanes,
      diagram: activeDiagram,
      activeView: diagramState,
      customShapeTypes,
    });
    downloadFile(xml, `${selectedNodeId}-${diagramState}.bpmn`, 'application/xml');
    showToast('Exported BPMN 2.0 XML', 'success');
    setExportPanelOpen(false);
  }

  function handleExportMermaid() {
    if (!selectedNode) return;
    const text = exportMermaid({ lanes, diagram: activeDiagram, customShapeTypes });
    downloadFile(text, `${selectedNodeId}-${diagramState}.mmd`, 'text/plain');
    showToast('Exported Mermaid flowchart', 'success');
    setExportPanelOpen(false);
  }

  async function handleImport(file) {
    if (!file || !selectedNodeId || readOnly) return;
    setSaving(true);
    try {
      const text = await file.text();
      const parsed = parseTransformaImport(text);
      const prepared = prepareProcessDesignImport(parsed, buildItems, lanes);
      const { error } = await sb.rpc('replace_process_design_diagrams', {
        p_project_id: project.id,
        p_process_node_id: selectedNodeId,
        p_lanes: prepared.lanes,
        p_diagrams: prepared.diagrams,
      });
      if (error) throw error;
      await load();
      showToast('Diagram imported', 'success');
    } catch (e) {
      console.error('[ProcessDesign] import failed', e);
      showToast('Import failed — not a recognised Transforma JSON file');
    } finally {
      setSaving(false);
    }
  }

  function buildItemTitle(ref) {
    const bi = buildItems.find((b) => b.ref === ref);
    return bi?.title || null;
  }

  const connMidpoints = useMemo(() => {
    const mids = [];
    for (const c of activeDiagram.connections) {
      const from = activeDiagram.shapes.find((s) => s.id === c.from);
      const to = activeDiagram.shapes.find((s) => s.id === c.to);
      if (!from || !to) continue;
      const a = shapeCenter(from, customShapeTypes);
      const b = shapeCenter(to, customShapeTypes);
      const x1 = a.x + a.w / 2 <= b.x ? a.x + a.w / 2 : a.x;
      const y1 = a.y;
      const x2 = b.x - b.w / 2 >= a.x ? b.x - b.w / 2 : b.x;
      const y2 = b.y;
      mids.push({ id: c.id, x: (x1 + x2) / 2, y: (y1 + y2) / 2, x1, y1, x2, y2, label: c.label });
    }
    return mids;
  }, [activeDiagram, customShapeTypes]);

  if (loading) return <Loader text="Loading process design…" />;

  const canvasH = lanes.length * LANE_HEIGHT;
  const viewLabel = activeView === 'delta'
    ? 'Delta (future vs current)'
    : (activeView === 'current' ? 'Current state' : 'Future state');

  return (
    <div className="pd-shell" onClick={() => { setLanePanelOpen(false); setExportPanelOpen(false); setConnDeleteId(null); }}>
      <div className="pd-head">
        <div className="pd-eyebrow">Workbench · Design</div>
        <div className="pd-title-row">
          Process Design
          <span className="wb-rex-chip">
            <span className="wb-rex-avatar">R</span>
            Rex proposes · PM adjusts depth
          </span>
        </div>
        <div className="pd-sub">
          Visio-style L3/L4 flows per domain — fully customisable shapes, lanes, and colours.
          Import/export to portable formats so diagrams aren&apos;t locked into Transforma.
        </div>
      </div>

      <div className="pd-domain-tabs">
        {domainTabs.map((d) => (
          <button
            key={d.id}
            type="button"
            className={`pd-domain-tab${d.id === activeDomainId ? ' on' : ''}`}
            onClick={() => { setActiveDomainId(d.id); setSelectedNodeId(null); }}
          >
            {d.name}
          </button>
        ))}
        {!domainTabs.length && (
          <span className="pd-sub" style={{ padding: '6px 0' }}>No in-scope process domains — complete Process scope in Initiation first.</span>
        )}
      </div>

      <div className="pd-body">
        <div className="pd-tree-panel">
          <div className="pd-tree-hdr">Process taxonomy</div>
          <div className="pd-tree-body">
            {treeRows.map(({ node, selectable, levelClass, status }) => (
              <div
                key={node.id}
                className={`pd-tree-row ${levelClass}${selectable && node.id === selectedNodeId ? ' selected' : ''}`}
                onClick={() => selectable && selectNode(node.id)}
              >
                <span className="pd-tree-ic">L{node.level}</span>
                {node.name}
                {selectable && (
                  <span className={`pd-tree-badge ${flowStatusBadgeClass(status)}`}>
                    {FLOW_STATUS_LABEL[status] || status}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="pd-canvas-panel">
          <div className="pd-toolbar" onClick={(e) => e.stopPropagation()}>
            <div className="pd-toolbar-group">
              <span className="pd-toolbar-label">View</span>
              {['future', 'current', 'delta'].map((v) => (
                <button key={v} type="button" className={`pd-seg-pill${activeView === v ? ' on' : ''}`} onClick={() => setView(v)}>
                  {v === 'future' ? 'Future state' : v === 'current' ? 'Current state' : 'Delta'}
                </button>
              ))}
            </div>
            <div className="pd-toolbar-group">
              <span className="pd-toolbar-label">Depth</span>
              {[3, 4].map((d) => (
                <button key={d} type="button" className={`pd-depth-pill${activeDepth === d ? ' on' : ''}`} onClick={() => setActiveDepth(d)}>
                  L{d}
                </button>
              ))}
            </div>
            <div className="pd-toolbar-group" style={{ position: 'relative' }}>
              <button type="button" className="pd-btn sm" disabled={isReadOnlyCanvas} onClick={() => setLanePanelOpen((o) => !o)}>
                ⇕ Swim lanes
              </button>
              {lanePanelOpen && selectedNodeId && (
                <div className="pd-float-panel">
                  <div className="pd-float-hdr">Swim lanes for this node</div>
                  {lanes.map((name, i) => (
                    <div key={name} className="pd-lane-row">
                      <input type="text" defaultValue={name} onBlur={(e) => renameLane(i, e.target.value)} />
                      <button type="button" disabled={i === 0} onClick={() => moveLane(i, -1)}>↑</button>
                      <button type="button" disabled={i === lanes.length - 1} onClick={() => moveLane(i, 1)}>↓</button>
                      <button type="button" onClick={() => deleteLane(i)}>✕</button>
                    </div>
                  ))}
                  <div className="pd-lane-row">
                    <button type="button" style={{ flex: 1, fontWeight: 600 }} onClick={addLane}>+ Add swim lane</button>
                  </div>
                </div>
              )}
            </div>
            <div className="pd-spacer" />
            {connectingFromId && <span className="pd-connect-hint">Click the shape to connect to…</span>}
            <div className="pd-toolbar-group" style={{ position: 'relative' }}>
              <button type="button" className="pd-btn sm" onClick={() => setExportPanelOpen((o) => !o)}>⇩ Export</button>
              {exportPanelOpen && (
                <div className="pd-float-panel">
                  <button type="button" className="pd-menu-item" onClick={handleExportJson}>Transforma JSON (.json) — lossless, re-importable</button>
                  <button type="button" className="pd-menu-item" onClick={handleExportBpmn}>BPMN 2.0 XML (.bpmn)</button>
                  <button type="button" className="pd-menu-item" onClick={handleExportMermaid}>Mermaid flowchart (.mmd)</button>
                  <div className="pd-menu-note">Native Visio (.vsdx) isn&apos;t generated — use BPMN XML for tool interop.</div>
                </div>
              )}
            </div>
            <button type="button" className="pd-btn sm" disabled={readOnly} onClick={() => importRef.current?.click()}>⇧ Import JSON</button>
            <input ref={importRef} type="file" accept=".json" style={{ display: 'none' }} onChange={(e) => { handleImport(e.target.files?.[0]); e.target.value = ''; }} />
            <button type="button" className="pd-btn sm primary" disabled={readOnly || saving} onClick={publishFlow}>Publish future-state flow</button>
          </div>

          {!isReadOnlyCanvas && (
            <div className="pd-palette">
              <span className="pd-palette-label">Add</span>
              {Object.entries(BUILTIN_SHAPE_DEFS).map(([type, def]) => (
                <button key={type} type="button" className="pd-pal-btn" onClick={() => addShape(type)}>
                  <span className={`pd-pal-swatch base-${def.base}`} style={{ background: def.color.bg, border: `1.5px solid ${def.color.border}` }} />
                  {def.label}
                </button>
              ))}
              {customShapeTypes.map((def) => (
                <button key={def.id} type="button" className="pd-pal-btn" onClick={() => addShape(def.id)}>
                  <span className={`pd-pal-swatch base-${def.base_geometry}`} style={{ background: def.color.bg, border: `1.5px solid ${def.color.border}` }} />
                  {def.label}
                </button>
              ))}
              <button type="button" className="pd-pal-btn custom-add" disabled={readOnly} onClick={() => setCustomModalOpen(true)}>+ Custom shape…</button>
            </div>
          )}

          {activeView === 'future' && futureStatus === 'review' && (
            <div className="pd-rex-banner">
              <span className="wb-rex-avatar">R</span>
              <span><b>Rex proposed this future-state flow</b> — review, adjust the diagram, then publish.</span>
              <span className="pd-rex-actions">
                <button type="button" className="pd-btn sm" disabled={readOnly} onClick={() => showToast('Adjustment noted — Rex will redraft', 'success')}>Request changes</button>
                <button type="button" className="pd-btn sm primary" disabled={readOnly} onClick={acceptDraft}>Accept draft</button>
              </span>
            </div>
          )}
          {activeView === 'future' && futureStatus === 'draft' && (
            <div className="pd-rex-banner plain">
              <span className="wb-rex-avatar" style={{ background: '#B4B2A9' }}>R</span>
              <span>Rex is still drafting this flow — check back shortly, or draft it manually on the canvas below.</span>
            </div>
          )}

          <div className="pd-scroll-wrap">
            {!selectedNode ? (
              <div className="pd-empty">Select a process node from the tree</div>
            ) : (
              <>
                <div className="pd-flow-title-row">
                  <div>
                    <div className="pd-flow-node-title">{selectedNode.name}</div>
                    <div className="pd-flow-node-path">
                      {parentName ? `${parentName} › ` : ''}{selectedNode.name} · {activeDepth === 3 ? 'L3 process view' : 'L4 step-level view'} · {viewLabel}
                    </div>
                  </div>
                  {activeView !== 'delta' && (
                    <span className={`pd-flow-status-pill ${flowStatusBadgeClass(activeView === 'future' ? futureStatus : 'not_drafted')}`}>
                      {FLOW_STATUS_LABEL[activeView === 'future' ? futureStatus : 'not_drafted']}
                    </span>
                  )}
                </div>

                {!activeDiagram.shapes.length ? (
                  <div className="pd-empty">No {activeView} diagram drafted for this node yet. Use the palette above to start one.</div>
                ) : (
                  <div className="pd-diagram-frame" style={{ width: CANVAS_W, height: canvasH }}>
                    {lanes.map((_, i) => (
                      <div key={i} className="pd-lane-band" style={{ top: i * LANE_HEIGHT, height: LANE_HEIGHT }} />
                    ))}
                    {lanes.map((laneName, i) => (
                      <div key={laneName} className="pd-lane-label-row" style={{ top: i * LANE_HEIGHT }}>
                        <input className="pd-lane-label-txt" defaultValue={laneName} disabled={isReadOnlyCanvas} onBlur={(e) => renameLane(i, e.target.value)} />
                      </div>
                    ))}

                    <svg className="pd-conn-svg" width={CANVAS_W} height={canvasH}>
                      <defs>
                        <marker id="pd-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                          <path d="M0,0 L6,3 L0,6 z" fill="#8a8779" />
                        </marker>
                      </defs>
                      {connMidpoints.map((mp) => (
                        <g key={mp.id}>
                          <path
                            className="pd-conn-hit"
                            d={`M${mp.x1},${mp.y1} C${mp.x},${mp.y1} ${mp.x},${mp.y2} ${mp.x2},${mp.y2}`}
                            fill="none"
                            stroke="#8a8779"
                            strokeWidth="1.6"
                            markerEnd="url(#pd-arrow)"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isReadOnlyCanvas) {
                                setConnDeleteId(mp.id);
                                setConnDeletePos({ x: mp.x + 12, y: mp.y - 30 });
                              }
                            }}
                          />
                          {mp.label && (
                            <>
                              <rect x={mp.x - 16} y={mp.y - 9} width="32" height="16" fill="#fff" stroke="#d3d1c7" rx="4" />
                              <text x={mp.x} y={mp.y + 3} fontSize="9.5" fontWeight="700" textAnchor="middle" fill="#3C3489">{mp.label}</text>
                            </>
                          )}
                        </g>
                      ))}
                    </svg>

                    {activeDiagram.shapes.map((shape) => {
                      const { w, h } = shapeSize(shape.type, customShapeTypes);
                      const def = shapeDef(shape.type, customShapeTypes) || { base: 'rect', color: presetByKey('neutral') };
                      const color = shape.color || def.color;
                      const dCls = deltaClass(activeView, shape);
                      const cls = ['pd-shape', `base-${def.base}`, dCls];
                      if (shape.id === selectedShapeId || shape.id === connectingFromId) cls.push('selected');
                      const useDeltaColors = activeView === 'delta' && dCls;
                      const refTitle = shape.ref ? buildItemTitle(shape.ref) : null;
                      return (
                        <div
                          key={shape.id}
                          className={cls.join(' ')}
                          style={{
                            left: shape.x,
                            top: shape.y,
                            width: w,
                            height: h,
                            ...(!useDeltaColors ? {
                              background: color.bg,
                              borderColor: color.border,
                              color: color.text,
                              '--pd-sb': color.border,
                            } : {}),
                          }}
                          onMouseDown={(e) => onShapeMouseDown(e, shape)}
                          onClick={(e) => { e.stopPropagation(); handleShapeClick(shape.id); }}
                        >
                          {shape.ref && (
                            <span className="pd-ref-tag" title={refTitle || undefined}>{shape.ref}</span>
                          )}
                          {activeView === 'delta' && shape.delta_status && shape.delta_status !== 'none' && (
                            <span className={`pd-delta-tag ${shape.delta_status}`}>
                              {{ added: 'New', changed: 'Changed', removed: 'Removed' }[shape.delta_status]}
                            </span>
                          )}
                          {editingLabelId === shape.id ? (
                            <input
                              className="pd-label-input"
                              autoFocus
                              defaultValue={shape.label}
                              onBlur={(e) => {
                                updateShapeFields(shape.id, { label: e.target.value.trim() || shape.label });
                                setEditingLabelId(null);
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            />
                          ) : (
                            <span>{shape.label}</span>
                          )}
                        </div>
                      );
                    })}

                    {connDeleteId && connDeletePos && (
                      <div
                        className="pd-conn-del-btn"
                        style={{ left: connDeletePos.x, top: connDeletePos.y }}
                        onClick={(e) => { e.stopPropagation(); deleteConnection(connDeleteId); }}
                      >
                        ✕
                      </div>
                    )}

                    {selectedShapeId && !isReadOnlyCanvas && (() => {
                      const shape = activeDiagram.shapes.find((s) => s.id === selectedShapeId);
                      if (!shape) return null;
                      const isRemark = shape.lane === null || shape.lane === undefined;
                      const currentColorKey = shape.color ? PRESETS.find((p) => p.bg === shape.color.bg)?.key : null;
                      return (
                        <div
                          className="pd-shape-toolbar"
                          style={{ left: shape.x, top: Math.max(0, shape.y - 84) }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="pd-st-row">
                            <button type="button" className="pd-st-btn" onClick={() => setEditingLabelId(shape.id)}>✎ Rename</button>
                            {!isRemark && (
                              <select className="pd-st-select" value={shape.lane || ''} onChange={(e) => updateShapeFields(shape.id, { lane: e.target.value })}>
                                {lanes.map((l) => <option key={l} value={l}>{l}</option>)}
                              </select>
                            )}
                            {!isRemark && (
                              <button type="button" className="pd-st-btn" onClick={() => { setConnectingFromId(shape.id); setSelectedShapeId(null); }}>→ Connect</button>
                            )}
                            <button type="button" className="pd-st-btn danger" onClick={() => deleteShape(shape.id)}>🗑</button>
                          </div>
                          {!isRemark && (
                            <div className="pd-st-row">
                              <span className="pd-st-btn" style={{ cursor: 'default', opacity: 0.8 }}>🔗 Build item</span>
                              <select
                                className="pd-st-select"
                                style={{ maxWidth: 180 }}
                                value={shape.build_item_id || (shape.ref_text ? '__custom__' : '')}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === '__custom__') {
                                    const entered = window.prompt('Custom reference (e.g. CS-6):', shape.ref_text || '');
                                    if (entered === null) return;
                                    updateShapeFields(shape.id, { build_item_id: null, ref_text: entered.trim() || null, ref: entered.trim() || null });
                                  } else if (!v) {
                                    updateShapeFields(shape.id, { build_item_id: null, ref_text: null, ref: null });
                                  } else {
                                    const bi = buildItems.find((b) => b.id === v);
                                    updateShapeFields(shape.id, { build_item_id: v, ref_text: null, ref: bi?.ref || null });
                                  }
                                }}
                              >
                                <option value="">— No link —</option>
                                {buildItems.map((b) => (
                                  <option key={b.id} value={b.id}>{b.ref} — {b.title}</option>
                                ))}
                                <option value="__custom__">{shape.ref_text && !shape.build_item_id ? `Custom: ${shape.ref_text}` : 'Custom reference…'}</option>
                              </select>
                            </div>
                          )}
                          {activeView === 'future' && (
                            <div className="pd-st-row">
                              <select
                                className="pd-st-select"
                                value={shape.delta_status || ''}
                                onChange={(e) => updateShapeFields(shape.id, { delta_status: e.target.value || null })}
                              >
                                <option value="">Delta: none</option>
                                <option value="added">Added</option>
                                <option value="changed">Changed</option>
                                <option value="removed">Removed</option>
                              </select>
                            </div>
                          )}
                          <div className="pd-st-row">
                            {PRESETS.map((p) => (
                              <span
                                key={p.key}
                                className={`pd-st-swatch${currentColorKey === p.key ? ' on' : ''}`}
                                style={{ background: p.bg, borderColor: p.border }}
                                onClick={() => updateShapeFields(shape.id, { color: p.key === 'default' ? null : p })}
                              />
                            ))}
                            <input
                              type="color"
                              className="pd-st-color-input"
                              onChange={(e) => updateShapeFields(shape.id, { color: { bg: e.target.value, border: e.target.value, text: luminanceText(e.target.value) } })}
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {activeView === 'delta' && activeDiagram.shapes.length > 0 && (
                  <div className="pd-legend">
                    <span className="pd-legend-item"><span className="pd-legend-swatch" style={{ background: '#e1f5ee', border: '1px solid #5dcaa5' }} />Added</span>
                    <span className="pd-legend-item"><span className="pd-legend-swatch" style={{ background: '#faeeda', border: '1px solid #fac775' }} />Changed</span>
                    <span className="pd-legend-item"><span className="pd-legend-swatch" style={{ background: '#FCEBEB', border: '1px solid #F5ACAB' }} />Removed</span>
                    <span className="pd-legend-item"><span className="pd-legend-swatch" style={{ background: '#fff', border: '1px solid #d3d1c7' }} />Unchanged</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {customModalOpen && (
        <div className="pd-modal-overlay" onClick={() => setCustomModalOpen(false)}>
          <div className="pd-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-hdr">New custom shape</div>
            <div className="pd-modal-body">
              <div className="pd-modal-row">
                <div className="pd-modal-label">Label</div>
                <input type="text" value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} placeholder="e.g. Output / Report" />
              </div>
              <div className="pd-modal-row">
                <div className="pd-modal-label">Base geometry</div>
                <select value={customBase} onChange={(e) => setCustomBase(e.target.value)}>
                  {BASE_GEOMETRIES.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
                </select>
              </div>
              <div className="pd-modal-row">
                <div className="pd-modal-label">Default colour</div>
                <div className="pd-swatch-grid">
                  {PRESETS.map((p) => (
                    <span
                      key={p.key}
                      className={`pd-swatch${customColorKey === p.key ? ' on' : ''}`}
                      style={{ background: p.bg, border: `1.5px solid ${p.border}` }}
                      onClick={() => setCustomColorKey(p.key)}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="pd-modal-foot">
              <button type="button" className="pd-btn" onClick={() => setCustomModalOpen(false)}>Cancel</button>
              <button type="button" className="pd-btn primary" onClick={saveCustomShape}>Save — adds to palette</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast show ${toast.type === 'success' ? 'success' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}
